/**
 * Merges API analytics with fallback computations from activity list.
 * Ensures no page is empty when the project has activities.
 */

import type { ScheduleActivity } from '@/types';
import type {
  ScheduleAnalytics,
  RiskAnalytics,
  WBSKPI,
  FloatDistributionBucket,
  CriticalPathData,
  RiskConcentration,
} from '@/types/analytics';
import {
  computeScheduleMetrics,
  computeWBSHotspots,
  computeCriticalActivities,
  computeMilestones,
  computeQualityChecks,
  type WBSHotspotRow,
  type CriticalActivityRow,
  type MilestoneRow,
  type QualityCheckRow,
  type ScheduleMetrics,
} from './analyticsCompute';

export type CompareMode = 'none' | 'baseline' | 'previous';

export interface AnalyticsApiPayloads {
  project?: Record<string, unknown> | null;
  schedule?: ScheduleAnalytics | null;
  resources?: Record<string, unknown> | null;
  risk?: RiskAnalytics | null;
}

export interface MergedAnalytics {
  /** Schedule Health */
  scheduleMetrics: ScheduleMetrics;
  projectFinishDate: string | null;
  criticalPathDurationDays: number | null;
  negativeFloatCount: number;
  criticalNearCriticalCount: number;
  highFloatPct: number;
  floatDistribution: FloatDistributionBucket[];
  /** Critical Path page */
  criticalActivities: CriticalActivityRow[];
  wbsHotspots: WBSHotspotRow[];
  /** Milestones page */
  milestones: MilestoneRow[];
  /** Quality page */
  qualityChecks: QualityCheckRow[];
  /** Raw for compatibility */
  wbsKpis: WBSKPI[];
  riskConcentrationByWbs: RiskConcentration[];
  /** Deltas when compare mode is on (optional) */
  delta?: {
    negativeFloatCount?: number;
    projectFinishDays?: number;
    criticalPathDurationDays?: number;
  };
  /** Data source for trust: API vs derived (fallback). Unknown status when derived. */
  dataSource?: {
    projectFinishDate: 'api' | 'derived';
    criticalPathDurationDays: 'api' | 'derived';
  };
  /** Errors collected for DataStatusPanel */
  errors: string[];
}

function isEmptySchedule(schedule: ScheduleAnalytics | null | undefined): boolean {
  if (!schedule) return true;
  const fd = schedule.float_distribution;
  const wbs = schedule.wbs_kpis ?? schedule.planned_vs_actual_by_wbs;
  const cp = schedule.critical_path;
  const hasFloat = Array.isArray(fd) && fd.length > 0;
  const hasWbs = Array.isArray(wbs) && wbs.length > 0;
  const hasCp = cp && (cp.critical_activities != null || cp.duration_days != null);
  return !hasFloat && !hasWbs && !hasCp;
}

function getNegativeFloatCountFromSchedule(schedule: ScheduleAnalytics | null | undefined): number {
  if (!schedule?.float_distribution || !Array.isArray(schedule.float_distribution)) return 0;
  const bucket = schedule.float_distribution.find((b: { bucket?: string }) => b.bucket === '<0');
  return bucket?.count ?? 0;
}

/**
 * Merge API payloads with fallback computed from activities.
 * When API returns null/empty for a metric, use computed value from activities.
 */
export function mergeAnalytics(
  payloads: AnalyticsApiPayloads,
  activities: ScheduleActivity[],
  options?: {
    compareMode?: CompareMode;
    baselineSchedule?: ScheduleAnalytics | null;
    missingPredecessorsCount?: number;
    missingSuccessorsCount?: number;
    constraintsCount?: number;
    excessiveLagsCount?: number;
    baselineOrOriginalByActivityId?: Record<string, string>;
  }
): MergedAnalytics {
  const errors: string[] = [];
  const schedule = payloads.schedule;
  const risk = payloads.risk;
  const computed = activities.length > 0 ? computeScheduleMetrics(activities) : null;

  const negativeFloatCount =
    (computed?.negativeFloatCount ?? 0) ||
    getNegativeFloatCountFromSchedule(schedule) ||
    0;
  const criticalNearCriticalCount =
    (computed?.criticalOrNearCriticalCount ?? 0) ||
    (schedule?.critical_path?.critical_activities ?? 0) ||
    0;
  const highFloatPct = computed?.highFloatPct ?? 0;
  const floatDistribution =
    (schedule?.float_distribution && Array.isArray(schedule.float_distribution) && schedule.float_distribution.length > 0)
      ? schedule.float_distribution
      : (computed?.floatDistribution ?? []);
  const cp = schedule?.critical_path;
  const hasApiFinish = cp?.finish_date != null && String(cp.finish_date).trim() !== '';
  const hasApiDuration = cp?.duration_days != null && !Number.isNaN(Number(cp.duration_days));
  const projectFinishDate =
    hasApiFinish && cp ? cp.finish_date! : (computed?.projectFinishDate ?? null);
  const criticalPathDurationDays =
    hasApiDuration && cp ? Number(cp.duration_days) : (computed?.criticalPathDurationDays ?? null);
  const dataSource = {
    projectFinishDate: hasApiFinish ? ('api' as const) : ('derived' as const),
    criticalPathDurationDays: hasApiDuration ? ('api' as const) : ('derived' as const),
  };

  const scheduleMetrics: ScheduleMetrics = computed ?? {
    minFloatDays: 0,
    negativeFloatCount: 0,
    criticalCount: 0,
    nearCriticalCount: 0,
    criticalOrNearCriticalCount: 0,
    highFloatPct: 0,
    floatDistribution: [],
    projectFinishDate: null,
    criticalPathDurationDays: null,
  };

  const wbsKpis: WBSKPI[] =
    (schedule?.wbs_kpis ?? schedule?.planned_vs_actual_by_wbs) && Array.isArray(schedule.wbs_kpis ?? schedule.planned_vs_actual_by_wbs)
      ? (schedule.wbs_kpis ?? schedule.planned_vs_actual_by_wbs)
      : [];

  const wbsHotspotsComputed = activities.length > 0 ? computeWBSHotspots(activities) : [];
  const wbsHotspotsFromApi = risk?.risk_concentration_by_wbs;
  const wbsHotspots: WBSHotspotRow[] =
    wbsHotspotsComputed.length > 0
      ? wbsHotspotsComputed
      : Array.isArray(wbsHotspotsFromApi) && wbsHotspotsFromApi.length > 0
        ? wbsHotspotsFromApi.map((r) => {
            const cnCount = r.critical_at_risk + (r.negative_float ?? 0);
            const overdue = r.overdue ?? 0;
            const hotspotScore = cnCount * 2 + overdue * 3;
            return {
              wbs_id: r.wbs_id,
              wbs_name: r.wbs_name,
              wbs_short_name: r.wbs_short_name,
              total_activities: 0,
              avgFloatDays: 0,
              minFloatDays: 0,
              criticalNearCriticalCount: cnCount,
              criticalNearCriticalPct: 0,
              overdueCount: overdue,
              hotspotScore,
            };
          })
        : [];

  const criticalActivities =
    activities.length > 0 ? computeCriticalActivities(activities) : [];

  const milestones =
    activities.length > 0
      ? computeMilestones(activities, options?.baselineOrOriginalByActivityId)
      : [];

  const missingPred = options?.missingPredecessorsCount ?? 0;
  const missingSucc = options?.missingSuccessorsCount ?? 0;
  const constraintsCount = options?.constraintsCount ?? 0;
  const excessiveLags = options?.excessiveLagsCount ?? 0;
  const qualityChecks = computeQualityChecks(
    activities,
    missingPred,
    missingSucc,
    constraintsCount,
    excessiveLags
  );

  const riskConcentrationByWbs: RiskConcentration[] =
    risk?.risk_concentration_by_wbs ?? [];

  let delta: MergedAnalytics['delta'];
  if (options?.compareMode === 'baseline' && options?.baselineSchedule) {
    const baseNeg = getNegativeFloatCountFromSchedule(options.baselineSchedule);
    const baseFinish = options.baselineSchedule.critical_path?.finish_date;
    const baseDuration = options.baselineSchedule.critical_path?.duration_days;
    delta = {
      negativeFloatCount: negativeFloatCount - baseNeg,
      projectFinishDays:
        projectFinishDate && baseFinish
          ? Math.round(
              (new Date(projectFinishDate).getTime() - new Date(baseFinish).getTime()) /
                (24 * 60 * 60 * 1000)
            )
          : undefined,
      criticalPathDurationDays:
        criticalPathDurationDays != null && baseDuration != null
          ? criticalPathDurationDays - baseDuration
          : undefined,
    };
  }

  return {
    scheduleMetrics,
    projectFinishDate,
    criticalPathDurationDays,
    negativeFloatCount,
    criticalNearCriticalCount,
    highFloatPct,
    floatDistribution,
    criticalActivities,
    wbsHotspots,
    milestones,
    qualityChecks,
    wbsKpis,
    riskConcentrationByWbs,
    delta,
    dataSource,
    errors,
  };
}
